// XML -> Record, the shape the mapping engine's dot-paths walk. A dependency-
// free tokenizer (no DOMParser, so it runs in tests and a worker alike) whose
// output must equal backend/internal/mappingengine/xmlrecord.go's - both are
// checked against the shared fixture corpus (xml-* cases).
//
//   document            -> { <rootName>: <element> }
//   text-only element   -> "trimmed text" ("" when empty)
//   element w/ children or attributes -> object:
//       attribute         -> "@name": "value"
//       child             -> "name": <element>; a repeated name becomes an array, in order
//       text alongside    -> "#text": "trimmed text" (omitted when empty)
// Names use the local part (namespace prefixes and xmlns attributes dropped).
// DOCTYPE is skipped and never expanded; only the five predefined and numeric
// entities are known - anything else is an error, as in Go's strict decoder.
import type { JsonValue } from './mappingEngine';

const local = (n: string) => n.slice(n.indexOf(':') + 1);
const NAMED: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };
const MAX_DEPTH = 64;

function decode(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[A-Za-z]+);/g, (_, e: string) => {
    if (e[0] === '#') {
      const cp = e[1] === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      if (!(cp > 0 && cp <= 0x10ffff)) throw new Error(`Invalid character reference &${e};`);
      return String.fromCodePoint(cp);
    }
    if (!(e in NAMED)) throw new Error(`Unknown entity &${e};`);
    return NAMED[e];
  });
}

interface Node {
  name: string;
  attrs: [string, string][];
  children: [string, JsonValue][];
  text: string;
}

const finish = (n: Node): JsonValue => {
  const text = n.text.trim();
  if (n.attrs.length === 0 && n.children.length === 0) return text;
  const obj: Record<string, JsonValue> = {};
  for (const [k, v] of n.attrs) obj[`@${k}`] = v;
  for (const [k, v] of n.children) {
    const cur = obj[k];
    if (cur === undefined) obj[k] = v;
    else if (Array.isArray(cur)) cur.push(v);
    else obj[k] = [cur, v];
  }
  if (text) obj['#text'] = text;
  return obj;
};

export function xmlToRecord(src: string): JsonValue {
  const stack: Node[] = [];
  let root: [string, JsonValue] | null = null;
  let i = 0;
  const fail = (m: string): never => {
    throw new Error(`XML: ${m}`);
  };
  const addText = (t: string) => {
    if (stack.length) stack[stack.length - 1].text += t;
    else if (t.trim()) fail('text outside the root element');
  };

  while (i < src.length) {
    if (src[i] !== '<') {
      const j = src.indexOf('<', i);
      const end = j < 0 ? src.length : j;
      addText(decode(src.slice(i, end)));
      i = end;
    } else if (src.startsWith('<!--', i)) {
      const j = src.indexOf('-->', i + 4);
      if (j < 0) fail('unterminated comment');
      i = j + 3;
    } else if (src.startsWith('<![CDATA[', i)) {
      const j = src.indexOf(']]>', i);
      if (j < 0) fail('unterminated CDATA');
      addText(src.slice(i + 9, j));
      i = j + 3;
    } else if (src.startsWith('<?', i)) {
      const j = src.indexOf('?>', i);
      if (j < 0) fail('unterminated processing instruction');
      i = j + 2;
    } else if (src.startsWith('<!', i)) {
      let depth = 0;
      let j = i + 2;
      for (; j < src.length; j++) {
        if (src[j] === '[') depth++;
        else if (src[j] === ']') depth--;
        else if (src[j] === '>' && depth <= 0) break;
      }
      if (j >= src.length) fail('unterminated declaration');
      i = j + 1;
    } else if (src[i + 1] === '/') {
      const j = src.indexOf('>', i);
      if (j < 0) fail('unterminated end tag');
      const name = local(src.slice(i + 2, j).trim());
      const node = stack.pop();
      if (!node || node.name !== name) fail(`unexpected </${name}>`);
      const value = finish(node!);
      if (stack.length) stack[stack.length - 1].children.push([node!.name, value]);
      else root = [node!.name, value];
      i = j + 1;
    } else {
      const m = /^<([^\s/>]+)((?:\s+[^\s=/>]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>/.exec(src.slice(i, src.indexOf('>', i) + 1));
      if (!m) fail('malformed start tag');
      if (stack.length >= MAX_DEPTH) fail('nesting too deep');
      const node: Node = { name: local(m![1]), attrs: [], children: [], text: '' };
      const attrRe = /([^\s=/>]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
      for (let a = attrRe.exec(m![2]); a; a = attrRe.exec(m![2])) {
        if (a[1] === 'xmlns' || a[1].startsWith('xmlns:')) continue;
        node.attrs.push([local(a[1]), decode(a[2] ?? a[3])]);
      }
      i += m![0].length;
      if (m![3]) {
        const value = finish(node);
        if (stack.length) stack[stack.length - 1].children.push([node.name, value]);
        else if (root) fail('multiple root elements');
        else root = [node.name, value];
      } else {
        if (!stack.length && root) fail('multiple root elements');
        stack.push(node);
      }
    }
  }
  if (stack.length) fail(`unclosed <${stack[stack.length - 1].name}>`);
  if (!root) fail('no root element');
  return { [root![0]]: root![1] };
}
